import React, { useState, useMemo, useEffect } from 'react';
import { FaFilter } from 'react-icons/fa';
import SearchBar from '@components/Layout/SearchBar';
import Pagination from '@components/Layout/Pagination';
import UserTable from '@components/Layout/UserTable';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { fetchCountries, userList } from '@utils/utils';
import { useNavigate } from 'react-router-dom';
import {
    CREATE_USER_PATH,
    INDIVIDUAL_USER_PATH,
    HOSPITAL_USER_PATH,
    NURSE_USER_PATH,
} from '@constants/routes';
import { INDIVIDUAL, NURSE, HOSPITAL, ADMIN } from '@constants/constants';
import { USER_ROLE } from '@constants/cognitoAttributes';
import { useDispatch } from 'react-redux';
import {
    setAdminCreateIndividual,
    setShowUpdatePassword,
    setUserRole,
    setDeviceUniqueID,
    setHospitalName,
    setHospitalBranch,
    setHospitalID,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setHospitalWebsite,
    setHospitalEmail,
    setHospitalContact,
    setHospitalGST,
    setFileMetadata,
    setName,
    setEmail,
    setMobileNumber,
    setPassword,
    setStateList,
    setCountryList,
} from '@store/reducers/auth/signupSlice';
import { getUserAttributes } from '@utils/authUtils';
import ModalPopUp from '@components/ModalPopUpComponent';
import { DynamoDBClient, QueryCommand } from '@aws-sdk/client-dynamodb';
import awsConfig from '../../../amplifyconfiguration.json';
import { dbClient } from '@utils/awsClients';
import DataLoader from '@components/DataLoader';
import Swal from 'sweetalert2';

const MySwal = Swal;

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

// type UserData = {
//     name: string;
//     deviceId: string;
//     email: string;
//     mobileNo: string;
//     userCreateDateString: string;
// };

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary py-2 !text-white"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));

interface UserData {
    id: string;
    email?: string;
    name?: string;
    deviceId?: string;
    mobileNo?: string;
    createdAt?: string;
    createdAt_time?: string;
    hospitalName?: string;
    hospitalContact?: string;
    status?: string;
    userEmailStatus?: string;
}

const IndividualUserList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState<UserData[]>([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [buttonText, setButtonText] = useState('Add Individual');
    // const [userRole, setUserRole] = useState('');



    const queryByTenantId = async (tenantId: string) => {
        const params = {
            TableName: 'hospitals',
            IndexName: 'tenant_id-created_at-index',
            KeyConditionExpression: 'tenant_id = :tenant_id',
            ExpressionAttributeValues: {
                ':tenant_id': { S: tenantId },
            },
        };

        const command = new QueryCommand(params);

        try {
            const data = await dbClient.send(command);
            // console.log('Success', data.Items);
        } catch (err) {
            console.error('Error', err);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await userList(INDIVIDUAL);
                setUserData(data);
            } catch (error: any) {
                let errorMessage = 'Unexpected error';

                switch (error.name) {
                    case 'ResourceNotFoundException':
                        errorMessage = 'Table or item not found.';
                        break;
                    case 'TooManyRequestsException':
                        errorMessage =
                            'Too many requests. Please try again later.';
                        break;
                    case 'ProvisionedThroughputExceededException':
                        errorMessage =
                            'Request limit exceeded. Please try again later.';
                        break;
                    case 'ServiceException':
                        errorMessage = `Service error: ${error.message}`;
                        break;
                    default:
                        errorMessage = `Unexpected error: ${error.message}`;
                        break;
                }
                MySwal.fire({
                    position: 'top-end', // Position the toast in the top-right corner
                    icon: 'error',
                    title: errorMessage,
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                    customClass: {
                        popup: '!text-red !bg-light_red', // Add custom class to the popup
                    },
                    width: 'auto',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // queryByTenantId('2b0b227d-b2c3-4d11-9a51-1938702a3085');
        handleFetchCountries();
        const updateButtonText = () => {
            const pathname = location.pathname;
            if (pathname == INDIVIDUAL_USER_PATH) {
                setButtonText('Add Individual');
            } else if (pathname == HOSPITAL_USER_PATH) {
                setButtonText('Add Hospital');
            } else if (pathname == NURSE_USER_PATH) {
                setButtonText('Add Nurse');
            }
        };
        updateButtonText();

        // Add event listener for changes in the history state
        // const handleHistoryChange = () => updateButtonText();
        // window.addEventListener('popstate', handleHistoryChange);

        // // Cleanup event listener on component unmount
        // return () => {
        //     window.removeEventListener('popstate', handleHistoryChange);
        // };
    }, [location.pathname]);

    const handleFetchCountries = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            // Handle error message
            console.error('Error fetching countries:', list.error);
        }
    };

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('individual_list_table.name'),
        },
        // {
        //     id: 2,
        //     title: t('individual_list_table.device_id'),
        // },
        {
            id: 2,
            title: t('individual_list_table.email_id'),
        },
        {
            id: 3,
            title: t('individual_list_table.contact_no'),
        },
        {
            id: 4,
            title: t('individual_list_table.create_date'),
        },
        {
            id: 5,
            title: t('individual_list_table.action'),
        },
        // Add more user objects here
    ]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSortField(event);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const filteredUsers = useMemo(() => {
        let filtered = userData;

        if (searchTerm) {
            filtered = filtered.filter(
                (user) =>
                    user.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.deviceId
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.email
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.mobileNo
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, userData]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt_time);
                const dateB = new Date(b.createdAt_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() === 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() === 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;

    async function handelAddUser() {
        // await getUserAttributes();
        dispatch(setUserRole(INDIVIDUAL));
        dispatch(setDeviceUniqueID(''));
        dispatch(setHospitalName(''));
        dispatch(setHospitalBranch(''));
        dispatch(setHospitalID(''));
        dispatch(setAddressOne(''));
        dispatch(setAddressTwo(''));
        dispatch(setCity(''));
        dispatch(setUserState(''));
        dispatch(setCountry(''));
        dispatch(setPincode(''));
        dispatch(setHospitalWebsite(''));
        dispatch(setHospitalEmail(''));
        dispatch(setHospitalContact(''));
        dispatch(setHospitalGST(''));
        dispatch(
            setFileMetadata({
                name: '',
                url: '',
                type: '',
            })
        );
        dispatch(setName(''));
        dispatch(setEmail(''));
        dispatch(setMobileNumber(''));
        dispatch(setPassword(''));
        dispatch(setAdminCreateIndividual(true));
        dispatch(setShowUpdatePassword(true));
        navigate(CREATE_USER_PATH);
    }

    return (
        <>
            <div className="2xl:min-w-full xl:min-w-[1030px] lg:min-w-[745px]  mx-auto">
                {/* <div className=""> */}
                <div className="flex justify-content-between align-items-center">
                    {/* <div className="flex items-center"> */}
                    <h1 className="text-lg font-semibold">
                        {t('user_management.individual_user.title')}
                    </h1>
                    <button
                        className="bg-primary rounded ml-4 btn py-2 px-3 text-white"
                        onClick={handelAddUser}
                    >
                        {buttonText}
                    </button>
                    {/* </div> */}
                </div>
                <div
                    className="flex justify-content-between
                 align-items-center my-4"
                >
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                    <Dropdown>
                        <Dropdown.Toggle
                            as={CustomDropdownToggle}
                            variant="primary"
                            id="dropdown-basic"
                        >
                            {t('user_management.individual_user.filter')}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Header>
                                {t('user_management.individual_user.order')}
                            </Dropdown.Header>
                            <Dropdown.Item
                                className="flex items-center"
                                onClick={() => handleSortChange('newest')}
                            >
                                <input
                                    type="radio"
                                    name="newest"
                                    value="newest"
                                    aria-label="newest"
                                    checked={
                                        sortField.toLowerCase() === 'newest'
                                    }
                                    className="mr-2"
                                    readOnly
                                />
                                {t('user_management.individual_user.newest')}
                            </Dropdown.Item>
                            <Dropdown.Item
                                className="flex items-center"
                                onClick={() => handleSortChange('oldest')}
                            >
                                <input
                                    type="radio"
                                    name="oldest"
                                    value="oldest"
                                    aria-label="oldest"
                                    checked={
                                        sortField.toLowerCase() === 'oldest'
                                    }
                                    className="mr-2"
                                    readOnly
                                />
                                {t('user_management.individual_user.oldest')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <UserTable data={paginatedUsers} header={tableHeading} row={rowsPerPage} totalData={userData} />
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default IndividualUserList;
